import type { IconName } from "@/components/ui/icon";
import type { Role, SessionUser } from "@/api/types";

export interface NavItem {
  key: string;
  label: string;
  icon: IconName;
  path: string;
  /** True for modules that exist in the product but have no backend behind them. */
  awaitingBackend?: boolean;
}

/**
 * Nav parity with the original HTML preview, one entry per module.
 *
 * Items flagged `awaitingBackend` render a real page with an honest empty state
 * rather than sample records: the Express API has no tables or routes for them
 * yet, and populating the screen with invented rows would make it lie.
 */
export const HR_NAV: NavItem[] = [
  { key: "overview", label: "Overview", icon: "overview", path: "/hr" },
  { key: "orgcalendar", label: "Org Calendar", icon: "calendar", path: "/hr/org-calendar" },
  { key: "attendance", label: "Attendance", icon: "attendance", path: "/hr/attendance" },
  { key: "leave", label: "Leave", icon: "leave", path: "/hr/leave" },
  { key: "analytics", label: "Analytics", icon: "analytics", path: "/hr/analytics" },
  { key: "teammentoria", label: "Team Mentoria", icon: "team", path: "/hr/team" },
  { key: "documents", label: "Documents & Compliance", icon: "documents", path: "/hr/documents" },
  { key: "useraccounts", label: "User Accounts", icon: "user-accounts", path: "/hr/accounts" },
  // HR & Admin is an employee of the company too. The backend has always
  // allowed this (leave.js: "self-service leave for everyone, including
  // HR/Admin, for their own leave"); only the nav was missing it.
  { key: "myattendance", label: "My attendance", icon: "attendance", path: "/hr/my-attendance" },
  { key: "myleave", label: "My leave", icon: "leave", path: "/hr/my-leave" },
  { key: "mydocuments", label: "My documents", icon: "documents", path: "/hr/my-documents" },
  {
    key: "performance",
    label: "Performance & KRA/KPI",
    icon: "performance",
    path: "/hr/performance",
    awaitingBackend: true,
  },
  {
    key: "lifecycle",
    label: "Employee lifecycle",
    icon: "lifecycle",
    path: "/hr/lifecycle",
    awaitingBackend: true,
  },
  {
    key: "recruitment",
    label: "Recruitment & hiring",
    icon: "recruitment",
    path: "/hr/recruitment",
    awaitingBackend: true,
  },
  {
    key: "hiringdash",
    label: "Hiring Dashboard",
    icon: "hiring-dashboard",
    path: "/hr/hiring-dashboard",
    awaitingBackend: true,
  },
  {
    key: "sentiment",
    label: "Sentiment tracking",
    icon: "sentiment",
    path: "/hr/sentiment",
    awaitingBackend: true,
  },
  { key: "access", label: "Access grants", icon: "access", path: "/hr/access", awaitingBackend: true },
];

export const MANAGER_NAV: NavItem[] = [
  { key: "overview", label: "Team overview", icon: "overview", path: "/manager" },
  { key: "orgcalendar", label: "Org Calendar", icon: "calendar", path: "/manager/org-calendar" },
  { key: "attendance", label: "Team attendance", icon: "attendance", path: "/manager/attendance" },
  { key: "leave", label: "Team leave", icon: "leave", path: "/manager/leave" },
  { key: "documents", label: "Team documents", icon: "documents", path: "/manager/documents" },
  // A manager is an employee of the company too — same reasoning as HR_NAV's
  // self-service items above.
  { key: "myattendance", label: "My attendance", icon: "attendance", path: "/manager/my-attendance" },
  { key: "myleave", label: "My leave", icon: "leave", path: "/manager/my-leave" },
  { key: "mydocuments", label: "My documents", icon: "documents", path: "/manager/my-documents" },
];

export const EMPLOYEE_NAV: NavItem[] = [
  { key: "overview", label: "My overview", icon: "overview", path: "/me" },
  { key: "orgcalendar", label: "Org Calendar", icon: "calendar", path: "/me/org-calendar" },
  { key: "attendance", label: "My attendance", icon: "attendance", path: "/me/attendance" },
  { key: "leave", label: "My leave", icon: "leave", path: "/me/leave" },
  { key: "documents", label: "My documents", icon: "documents", path: "/me/documents" },
];

export function navFor(role: Role): NavItem[] {
  if (role === "admin") return HR_NAV;
  if (role === "manager") return MANAGER_NAV;
  return EMPLOYEE_NAV;
}

/**
 * The nav a signed-in user actually sees. Same as navFor(role), except
 * People-Function approver admins (Nikhar/Juhi) don't see the awaitingBackend
 * modules at all — not just without their "soon" badge — since those modules
 * aren't part of what they need day to day. The primary HR & Admin account
 * (Vyomi) and any other admin keep seeing everything navFor(role) returns.
 */
export function visibleNavFor(user: SessionUser): NavItem[] {
  const items = navFor(user.role);
  if (user.role === "admin" && user.is_people_function_approver) {
    return items.filter((item) => !item.awaitingBackend);
  }
  return items;
}

/**
 * Where a notification's `link` should actually take this user.
 *
 * The backend writes a role-neutral "/leave", because it cannot know which of
 * the three leave screens the recipient uses — and work-from-home notices point
 * there too, since the WFH panels live inside the leave pages. There is no
 * `/leave` route for anyone, so navigating to it verbatim hit the catch-all in
 * App.tsx and silently bounced every notification to the user's home page.
 *
 * Rows already in the database carry "/leave", so this mapping has to keep
 * working for them whatever the backend writes from now on. Anything that is
 * neither a known key nor an already-role-scoped path resolves to null, so a
 * future bad link disables the click rather than redirecting somewhere wrong.
 */
export function resolveNotificationLink(link: string | null | undefined, role: Role): string | null {
  if (!link) return null;
  if (link === "/leave") {
    if (role === "admin") return "/hr/leave";
    if (role === "manager") return "/manager/leave";
    return "/me/leave";
  }
  const scoped = ["/hr/", "/manager/", "/me/"].some((prefix) => link.startsWith(prefix));
  return scoped ? link : null;
}

export function homePathFor(role: Role): string {
  if (role === "admin") return "/hr";
  if (role === "manager") return "/manager";
  return "/me";
}
